import {
  Component,
  OnInit,
  Input,
  EventEmitter,
  Output,
  signal,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import {
  GetRolesAndPermissionsContextGQL,
  GetUserAccountStatusGQL,
  OriginatorQueryInput,
  PermissionSummaryFieldsFragment,
} from 'src/app/graphql/generated';
import { rolesAndPermissionsPayload } from '../../utils/roles-permissions-query.util';
import { IUserStatus } from '../../models/user-status.interface';
import { DateUtilsService } from '../../services/utils/date-utils/date-utils.service';

@Component({
  selector: 'app-user-roles',
  templateUrl: './user-roles.component.html',
  styleUrls: ['./user-roles.component.scss'],
  standalone: false,
})
export class UserRolesComponent implements OnInit {
  status: string = '';
  @Input() userRoles: any;
  @Input() isPageName: any;
  @Input() originatorId!: string;
  @Input() isDisable: boolean = false;
  options!: any;
  @Input() selectedRoles: Array<string> = [];
  @Input() selectedUser: any;
  roles: any[] = [];
  @Input() userRoleFormGroup!: FormGroup<any>;
  @Output() isTenantMfaEnabled: EventEmitter<boolean> = new EventEmitter();
  @Output() isSsoEnabledChange: EventEmitter<boolean> = new EventEmitter();
  permissionGroups: Array<any> = [];
  roleAndPermissions: any;
  expandedRows: boolean[] = [];
  isReadOnly: boolean = false;
  tenantMfaEnabled: boolean = false;

  ssoEnabled = signal<boolean>(true);

  userStatus!: IUserStatus;
  isMfaEnable!: boolean;
  lastLoggedInDateTime!: string;
  @Input('tenant-views-subtenant') tenantViewsSubtenant: boolean = false;

  constructor(
    private readonly getRolesAndPermissionsContextGql: GetRolesAndPermissionsContextGQL,
    private readonly getUserAccountStatusGql: GetUserAccountStatusGQL,
    private readonly dateUtils: DateUtilsService
  ) {}

  ngOnInit(): void {
    if (this.isPageName === 'profile') {
      this.isReadOnly = true;
    }
    const originatorId = this.originatorId?.trim()
      ? this.originatorId.trim()
      : null;
    const originatorInput: OriginatorQueryInput | null = originatorId
      ? { originatorId: this.originatorId.trim() }
      : null;

    this.getRolesAndPermissionsContextGql
      .fetch({
        variables: {
          originatorInput,
        },
      })
      .subscribe(({ data }) => {
        if (!data) {
          return;
        }
        const payload = rolesAndPermissionsPayload(data);
        if (payload) {
          this.groupPermissions(payload.permissions);
          this.roleAndPermissions = payload;
          this.roles = payload.roles;
          this.tenantMfaEnabled = !!payload.isTenantMfaEnabled;

          this.ssoEnabled.set(payload.isSsoEnabled ?? false);

          this.selectedRoles = this.selectedRoles.filter((roleId: string) => {
            return Boolean(
              this.roles.find((role: any) => role.roleId === roleId)
            );
          });
          this.userRoleFormGroup?.get('role')?.setValue(this.selectedRoles);

          if (this.isDisable) {
            const roleControl = this.userRoleFormGroup?.get('role');
            if (roleControl) {
              roleControl.disable();
            }
          }
          let event = { value: this.selectedRoles };
          this.checkPermissions(event);
          this.isTenantMfaEnabled.emit(!!payload.isTenantMfaEnabled);
          this.isSsoEnabledChange.emit(payload.isSsoEnabled ?? false);
        }
      });
    this.getUserStatus();
  }

  groupPermissions(rolesPermissions: PermissionSummaryFieldsFragment[]) {
    rolesPermissions?.forEach((permission) => {
      let index = this.permissionGroups?.findIndex(
        (pg) => permission.groupName === pg.groupName
      );
      if (index >= 0) {
        this.permissionGroups[index]?.permissions.push(permission);
      } else {
        let permissionGroup: any = {};
        permissionGroup.groupName = permission.groupName;
        permissionGroup.permissions = [];
        permissionGroup.permissions.push({ ...permission, checked: false });
        this.permissionGroups?.push(permissionGroup);
      }
    });
  }

  getAllRoles(id: string): any {
    return this.roles?.find((role: any) => role.roleId === id);
  }

  checkPermissions(event: any) {
    if (event.value) {
      this.selectedRoles = event.value;
    }
    this.permissionGroups?.forEach((permissionGroup: any) => {
      permissionGroup.permissions?.forEach((permission: any) => {
        permission.checked = false;
        this.selectedRoles?.forEach((value: string) => {
          let role = this.getAllRoles(value);
          if (role) {
            let index = role.permissions.findIndex(
              (p: any) => p === permission.permissionName
            );
            if (index >= 0) {
              permission.checked = true;
            }
          }
        });
      });
    });
  }

  get roleArray(): FormArray {
    return this.userRoleFormGroup?.get('roleArray') as FormArray;
  }

  getRolesName(roleIds: any): string {
    return roleIds
      .map(
        (roleId: any) =>
          this.roleAndPermissions?.roles?.find(
            (role: any) => role.roleId === roleId
          )?.roleName
      )
      .filter(Boolean);
  }

  getFormGroup(rowIndex: number): FormGroup {
    return this.roleArray.get(rowIndex.toString()) as FormGroup;
  }

  toggleRow(rowIndex: number) {
    this.expandedRows[rowIndex] = !this.expandedRows[rowIndex];
    let event = { value: this.selectedRoles };
    this.checkPermissions(event);
  }

  get isAdminPage() {
    return this.isPageName === 'admin';
  }

  get isProfilePage() {
    return this.isPageName === 'profile';
  }

  getUserStatus() {
    if (this.selectedUser) {
      this.getUserAccountStatusGql
        .fetch({
          variables: {
            userId: this.selectedUser.userId,
            originatorInput: this.originatorId.trim()
              ? { originatorId: this.originatorId }
              : null,
          },
        })
        .subscribe(({ data }) => {
          const userStatus = data?.user?.userAccountStatus;
          if (userStatus) {
            this.isMfaEnable = !!userStatus.is2FaEnabled;
            this.status =
              userStatus.isBlocked || userStatus.isBruteForceBlocked
                ? 'Blocked'
                : 'Active';
            this.lastLoggedInDateTime =
              this.dateUtils.convertDateTimeISOForDisplay(
                userStatus.lastLoggedInDateTime
              );
          }
        });
    }
  }
}
